/**
 * Multi-Timeframe RSI
 *
 * Plots RSI at multiple periods to simulate multi-timeframe
 * momentum analysis on a single chart.
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

function computeRSI(closes: number[], period: number): (number | undefined)[] {
  const rsi: (number | undefined)[] = new Array(closes.length).fill(undefined);
  let avgGain = 0, avgLoss = 0;

  for (let i = 1; i <= period && i < closes.length; i++) {
    const change = closes[i] - closes[i - 1];
    if (change > 0) avgGain += change;
    else avgLoss += Math.abs(change);
  }
  avgGain /= period;
  avgLoss /= period;

  if (period < closes.length) {
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    rsi[period] = 100 - 100 / (1 + rs);
  }

  for (let i = period + 1; i < closes.length; i++) {
    const change = closes[i] - closes[i - 1];
    avgGain = (avgGain * (period - 1) + Math.max(change, 0)) / period;
    avgLoss = (avgLoss * (period - 1) + Math.max(-change, 0)) / period;
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    rsi[i] = 100 - 100 / (1 + rs);
  }
  return rsi;
}

export const mtfRsiIndicator: IndicatorDefinition = {
  id: "mtf_rsi",
  name: "Multi-TF RSI",
  shortName: "MTF RSI",
  category: "advanced",
  pane: "separate",
  params: [
    { key: "period1", label: "Fast Period", type: "number", default: 7, min: 2, max: 50 },
    { key: "period2", label: "Medium Period", type: "number", default: 14, min: 2, max: 50 },
    { key: "period3", label: "Slow Period", type: "number", default: 21, min: 2, max: 100 },
  ],
  plots: [
    { key: "rsi1", label: "Fast RSI", color: "#4CAF50", style: "line", lineWidth: 1 },
    { key: "rsi2", label: "Medium RSI", color: "#2196F3", style: "line", lineWidth: 2 },
    { key: "rsi3", label: "Slow RSI", color: "#F44336", style: "line", lineWidth: 1 },
    { key: "ob", label: "OB", color: "#555555", style: "line", lineWidth: 1 },
    { key: "os", label: "OS", color: "#555555", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period1, period2, period3 } = params;
    const closes = candles.map(c => c.close);
    const periods = [period1, period2, period3];
    const keys = ["rsi1", "rsi2", "rsi3"];

    const plots: PlotData[] = keys.map((key, idx) => {
      const rsi = computeRSI(closes, periods[idx]);
      const data: { time: number; value: number }[] = [];
      for (let i = 0; i < candles.length; i++) {
        if (rsi[i] !== undefined) {
          data.push({ time: candles[i].time, value: rsi[i]! });
        }
      }
      return { key, data };
    });

    // OB/OS reference lines
    const obData: { time: number; value: number }[] = [];
    const osData: { time: number; value: number }[] = [];
    const startIdx = Math.max(period1, period2, period3);
    for (let i = startIdx; i < candles.length; i++) {
      obData.push({ time: candles[i].time, value: 70 });
      osData.push({ time: candles[i].time, value: 30 });
    }

    plots.push({ key: "ob", data: obData });
    plots.push({ key: "os", data: osData });

    return plots;
  },
};
